import { supabase } from './supabase';
import { logger } from './logger';

// ─── Types ───────────────────────────────────────────────────────────

export interface RefItem {
  id: string;
  code: string;
  nameFr: string;
  nameEn: string;
  nameAr: string;
  nameTr: string;
  nameEs: string;
  namePt: string;
  nameVi: string;
  active: boolean;
  sortOrder: number;
  translated: boolean;
  translationLocked: Record<string, boolean> | null;
  sectorId?: string;
  subsectorId?: string;
  positionId?: string;
  category?: string;
  skillGroupId?: string;
}

export const TRANSLATION_LANGS = [
  { key: 'en', label: 'Anglais' },
  { key: 'ar', label: 'Arabe' },
  { key: 'tr', label: 'Turc' },
  { key: 'es', label: 'Espagnol' },
  { key: 'pt', label: 'Portugais' },
  { key: 'vi', label: 'Vietnamien' },
] as const;

export interface SkillGroup {
  id: string;
  positionId: string;
  code: string;
  nameFr: string;
  nameEn?: string;
  sortOrder: number;
  active: boolean;
  skills?: RefItem[];
}

export type SkillFamilySource = 'manual' | 'auto_label';
export type SkillFamilyReviewStatus = 'pending' | 'confirmed' | 'dismissed';

export interface SkillFamily {
  id: string;
  code: string;
  nameFr: string;
  nameEn?: string;
  nameAr?: string;
  nameTr?: string;
  nameEs?: string;
  namePt?: string;
  nameVi?: string;
  description?: string;
  active: boolean;
  source: SkillFamilySource;
  reviewStatus: SkillFamilyReviewStatus;
  normalizedLabel?: string | null;
  memberCount?: number;
  members?: RefItem[];
  translated?: boolean;
  translationLocked?: Record<string, boolean> | null;
}

/** Membre d'une famille avec contexte métier (secteur / sous-secteur / poste). */
export interface FamilyMember extends RefItem {
  sectorName?: string;
  sectorCode?: string;
  subsectorName?: string;
  positionName?: string;
  positionCode?: string;
}

export interface SkillUsageStat {
  skillId: string;
  skillName: string;
  checkedCount: number;
  uncheckedCount: number;
  ratio: number;
  lastUpdated: string;
}

export interface SkillProximityPair {
  id: string;
  skillA: { id: string; nameFr: string; code: string };
  skillB: { id: string; nameFr: string; code: string };
  similarity: number;
  status: 'pending' | 'confirmed' | 'merged' | 'dismissed';
  reviewedAt?: string;
}

export type RefTable = 'sectors' | 'subsectors' | 'positions' | 'skills' | 'skill_groups' | 'skill_families' | 'certifications';

// ─── Mappers ─────────────────────────────────────────────────────────

function mapRowToRefItem(r: any): RefItem {
  return {
    id: r.id,
    code: r.code,
    nameFr: r.name_fr || '',
    nameEn: r.name_en || '',
    nameAr: r.name_ar || '',
    nameTr: r.name_tr || '',
    nameEs: r.name_es || '',
    namePt: r.name_pt || '',
    nameVi: r.name_vi || '',
    active: r.active ?? true,
    sortOrder: r.sort_order || 0,
    translated: !!(r.name_en && r.name_en.trim()),
    translationLocked: r.translation_locked || null,
    sectorId: r.sector_id,
    subsectorId: r.subsector_id,
    positionId: r.position_id,
    category: r.category,
    skillGroupId: r.skill_group_id,
  };
}

function mapRefItemToRow(item: Partial<Omit<RefItem, 'id'>>): Record<string, any> {
  const row: Record<string, any> = {};
  if (item.code !== undefined) row.code = item.code;
  if (item.nameFr !== undefined) row.name_fr = item.nameFr;
  if (item.nameEn !== undefined) row.name_en = item.nameEn;
  if (item.nameAr !== undefined) row.name_ar = item.nameAr;
  if (item.active !== undefined) row.active = item.active;
  if (item.sortOrder !== undefined) row.sort_order = item.sortOrder;
  if (item.sectorId !== undefined) row.sector_id = item.sectorId || null;
  if (item.subsectorId !== undefined) row.subsector_id = item.subsectorId || null;
  if (item.positionId !== undefined) row.position_id = item.positionId || null;
  if (item.category !== undefined) row.category = item.category || null;
  if (item.skillGroupId !== undefined) row.skill_group_id = item.skillGroupId || null;
  return row;
}

// ─── CRUD Generic ────────────────────────────────────────────────────

export async function fetchReferential(table: RefTable): Promise<RefItem[]> {
  let query = supabase.from(table).select('*');

  // Only order by sort_order if the table has that column
  const tablesWithSortOrder: RefTable[] = ['subsectors', 'positions', 'skills', 'skill_groups', 'certifications'];
  if (tablesWithSortOrder.includes(table)) {
    query = query.order('sort_order', { ascending: true });
  } else {
    query = query.order('name_fr', { ascending: true });
  }

  const { data, error } = await query;

  if (error) {
    logger.error('fetch_referential', 'referentialService', { table, error: error.message });
    throw error;
  }
  logger.technical('fetch_referential_ok', 'referentialService', { table, count: (data || []).length });
  return (data || []).map(mapRowToRefItem);
}

export async function createReferentialItem(
  table: RefTable,
  item: Omit<RefItem, 'id'>
): Promise<RefItem> {
  const row = mapRefItemToRow(item);
  logger.functional('create_referential_item', 'referentialService', { table, code: item.code });

  const { data, error } = await supabase
    .from(table)
    .insert(row)
    .select()
    .single();

  if (error) {
    logger.error('create_referential_item', 'referentialService', { table, error: error.message });
    throw error;
  }
  logger.technical('create_referential_item_ok', 'referentialService', { table, id: data.id });
  triggerTranslation(table as TranslatableTable, [data.id]);
  return mapRowToRefItem(data);
}

export async function updateReferentialItem(
  table: RefTable,
  id: string,
  item: Partial<Omit<RefItem, 'id'>>
): Promise<void> {
  const row = mapRefItemToRow(item);
  logger.functional('update_referential_item', 'referentialService', { table, id });

  const { error } = await supabase
    .from(table)
    .update(row)
    .eq('id', id);

  if (error) {
    logger.error('update_referential_item', 'referentialService', { table, id, error: error.message });
    throw error;
  }
  logger.technical('update_referential_item_ok', 'referentialService', { table, id });
  if (item.nameFr !== undefined) {
    triggerTranslation(table as TranslatableTable, [id]);
  }
}

export async function deleteReferentialItem(table: RefTable, id: string): Promise<void> {
  logger.functional('delete_referential_item', 'referentialService', { table, id });
  const { error } = await supabase
    .from(table)
    .delete()
    .eq('id', id);

  if (error) {
    logger.error('delete_referential_item', 'referentialService', { table, id, error: error.message });
    throw error;
  }
  logger.technical('delete_referential_item_ok', 'referentialService', { table, id });
}

export async function toggleReferentialActive(table: RefTable, id: string, active: boolean): Promise<void> {
  logger.functional('toggle_referential_active', 'referentialService', { table, id, active });
  const { error } = await supabase
    .from(table)
    .update({ active })
    .eq('id', id);

  if (error) {
    logger.error('toggle_referential_active', 'referentialService', { table, id, error: error.message });
    throw error;
  }
}

/**
 * Réattribue les sort_order d'une fratrie après un déplacement.
 * Les valeurs sont renumérotées de 0 à n pour éviter les ex æquo hérités
 * des imports et des créations rapides (qui posent toutes sort_order = 0).
 */
export async function reorderReferentialItems(
  table: RefTable,
  items: { id: string; sortOrder: number }[],
): Promise<void> {
  if (items.length === 0) return;
  logger.functional('reorder_referential_items', 'referentialService', { table, count: items.length });

  const results = await Promise.all(
    items.map((it) => supabase.from(table).update({ sort_order: it.sortOrder }).eq('id', it.id)),
  );

  const failed = results.find((r) => r.error);
  if (failed?.error) {
    logger.error('reorder_referential_items', 'referentialService', { table, error: failed.error.message });
    throw failed.error;
  }
  logger.technical('reorder_referential_items_ok', 'referentialService', { table, count: items.length });
}

// ─── Skill Groups ────────────────────────────────────────────────────

export async function fetchSkillGroups(positionId?: string): Promise<SkillGroup[]> {
  let query = supabase
    .from('skill_groups')
    .select('*')
    .order('sort_order', { ascending: true });

  if (positionId) query = query.eq('position_id', positionId);

  const { data, error } = await query;
  if (error) throw error;

  return (data || []).map((r: any) => ({
    id: r.id,
    positionId: r.position_id,
    code: r.code,
    nameFr: r.name_fr,
    nameEn: r.name_en,
    sortOrder: r.sort_order || 0,
    active: r.active ?? true,
  }));
}

/**
 * Recalcul de la proximité par lots (le recalcul global dépasse le
 * statement_timeout depuis que le référentiel compte ~38 000 compétences).
 * Purge les paires pending puis traite chaque secteur par tranches.
 */
export async function recomputeProximityChunked(
  threshold: number,
  onProgress?: (msg: string) => void,
): Promise<number> {
  const CHUNK = 400;
  const { data: sectors, error: secErr } = await supabase
    .from('sectors').select('id, name_fr').eq('active', true);
  if (secErr) throw secErr;

  const { error: purgeErr } = await supabase.rpc('purge_pending_proximity_pairs', {});
  if (purgeErr) throw purgeErr;

  let total = 0;
  for (const sector of sectors || []) {
    const { count } = await supabase
      .from('skills')
      .select('id', { count: 'exact', head: true })
      .eq('sector_id', sector.id)
      .eq('active', true);
    const skillCount = count || 0;

    for (let offset = 0; offset < skillCount; offset += CHUNK) {
      onProgress?.(`${sector.name_fr} : ${Math.min(offset + CHUNK, skillCount)}/${skillCount} compétences…`);
      const { data, error } = await supabase.rpc('compute_skill_proximity_chunk', {
        p_sector_id: sector.id,
        p_offset: offset,
        p_limit: CHUNK,
        threshold,
      });
      if (error) throw error;
      total += data || 0;
    }
  }
  logger.functional('recompute_proximity_chunked', 'referentialService', { threshold, pairs: total });
  return total;
}

/** Groupes de compétences d'un poste (évite de charger les ~5000 groupes). */
export async function fetchGroupsByPosition(positionId: string): Promise<RefItem[]> {
  const { data, error } = await supabase
    .from('skill_groups')
    .select('*')
    .eq('position_id', positionId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapRowToRefItem);
}

/**
 * Recherche globale de compétences par libellé.
 * Retourne les ids des postes possédant au moins une compétence correspondante.
 */
export async function searchSkillPositions(query: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('skills')
    .select('position_id')
    .ilike('name_fr', `%${query}%`)
    .not('position_id', 'is', null)
    .limit(2000);

  if (error) throw error;
  return new Set((data || []).map((r: any) => r.position_id as string));
}

export async function fetchSkillsByGroup(groupId: string): Promise<RefItem[]> {
  const { data, error } = await supabase
    .from('skills')
    .select('*')
    .eq('skill_group_id', groupId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapRowToRefItem);
}

// ─── Skill Families ──────────────────────────────────────────────────

function mapSkillFamily(r: any): SkillFamily {
  return {
    id: r.id,
    code: r.code,
    nameFr: r.name_fr,
    nameEn: r.name_en,
    nameAr: r.name_ar,
    nameTr: r.name_tr,
    nameEs: r.name_es,
    namePt: r.name_pt,
    nameVi: r.name_vi,
    description: r.description,
    active: r.active ?? true,
    source: (r.source as SkillFamilySource) || 'manual',
    reviewStatus: (r.review_status as SkillFamilyReviewStatus) || 'confirmed',
    normalizedLabel: r.normalized_label ?? null,
    memberCount: Array.isArray(r.skill_family_members)
      ? r.skill_family_members.length
      : (r.memberCount ?? 0),
    translated: !!(r.name_en && r.name_en.trim()),
    translationLocked: r.translation_locked || null,
  };
}

export async function fetchSkillFamilies(opts?: {
  source?: SkillFamilySource | 'all';
  reviewStatus?: SkillFamilyReviewStatus | 'all';
}): Promise<SkillFamily[]> {
  const PAGE = 1000;
  const all: SkillFamily[] = [];

  for (let from = 0; ; from += PAGE) {
    let query = supabase
      .from('skill_families')
      .select('*, skill_family_members(skill_id)')
      .order('name_fr', { ascending: true })
      .range(from, from + PAGE - 1);

    if (opts?.source && opts.source !== 'all') {
      query = query.eq('source', opts.source);
    }
    if (opts?.reviewStatus && opts.reviewStatus !== 'all') {
      query = query.eq('review_status', opts.reviewStatus);
    }

    const { data, error } = await query;
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...data.map(mapSkillFamily));
    if (data.length < PAGE) break;
  }

  return all;
}

/**
 * IDs des familles sans couverture complète (7 langues).
 * Paginé au-delà de la limite PostgREST (1000).
 */
export async function fetchSkillFamilyIdsMissingTranslations(): Promise<string[]> {
  const PAGE = 1000;
  const ids: string[] = [];

  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('skill_families')
      .select('id, name_en, name_ar, name_tr, name_es, name_pt, name_vi')
      .order('id')
      .range(from, from + PAGE - 1);

    if (error) throw error;
    if (!data || data.length === 0) break;

    for (const row of data as any[]) {
      const incomplete = !row.name_en || !row.name_ar || !row.name_tr
        || !row.name_es || !row.name_pt || !row.name_vi;
      if (incomplete) ids.push(row.id);
    }
    if (data.length < PAGE) break;
  }

  return ids;
}

/** Tire un échantillon aléatoire de familles auto en attente de review. */
export async function fetchPendingFamilySample(limit = 20): Promise<SkillFamily[]> {
  const { data, error } = await supabase
    .from('skill_families')
    .select('*, skill_family_members(skill_id)')
    .eq('source', 'auto_label')
    .eq('review_status', 'pending')
    .eq('active', true)
    .limit(Math.min(200, Math.max(limit * 5, limit)));

  if (error) throw error;
  const mapped = (data || []).map(mapSkillFamily);
  // Mélange côté client (PostgREST n'a pas de ORDER BY random() fiable via API)
  for (let i = mapped.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [mapped[i], mapped[j]] = [mapped[j], mapped[i]];
  }
  return mapped.slice(0, limit);
}

export async function reviewSkillFamily(
  id: string,
  action: 'confirmed' | 'dismissed',
): Promise<void> {
  const row: Record<string, unknown> = {
    review_status: action,
    active: action === 'confirmed',
  };
  // Confirmé → active ; dismissé → inactive (retirée du matching)
  if (action === 'confirmed') row.active = true;
  if (action === 'dismissed') row.active = false;

  const { error } = await supabase
    .from('skill_families')
    .update(row)
    .eq('id', id);

  if (error) throw error;
  logger.functional('review_skill_family', 'referentialService', { id, action });
  if (action === 'confirmed') {
    triggerTranslation('skill_families', [id]);
  }
}

export async function fetchFamilyReviewStats(): Promise<{
  pending: number;
  confirmed: number;
  dismissed: number;
  manual: number;
}> {
  const [pending, confirmed, dismissed, manual] = await Promise.all([
    supabase.from('skill_families').select('id', { count: 'exact', head: true })
      .eq('source', 'auto_label').eq('review_status', 'pending'),
    supabase.from('skill_families').select('id', { count: 'exact', head: true })
      .eq('source', 'auto_label').eq('review_status', 'confirmed'),
    supabase.from('skill_families').select('id', { count: 'exact', head: true })
      .eq('source', 'auto_label').eq('review_status', 'dismissed'),
    supabase.from('skill_families').select('id', { count: 'exact', head: true })
      .eq('source', 'manual'),
  ]);
  return {
    pending: pending.count ?? 0,
    confirmed: confirmed.count ?? 0,
    dismissed: dismissed.count ?? 0,
    manual: manual.count ?? 0,
  };
}

export async function fetchFamilyMembers(familyId: string): Promise<FamilyMember[]> {
  const { data, error } = await supabase
    .from('skill_family_members')
    .select(`
      skill_id,
      skills(
        *,
        sector:sectors!skills_sector_id_fkey(id, code, name_fr),
        position:positions!skills_position_id_fkey(
          id, code, name_fr,
          subsector:subsectors!positions_subsector_id_fkey(id, name_fr, category)
        )
      )
    `)
    .eq('family_id', familyId);

  if (error) {
    // Fallback si les noms de FK diffèrent selon l'environnement
    const fallback = await supabase
      .from('skill_family_members')
      .select(`
        skill_id,
        skills(
          *,
          sector:sectors(id, code, name_fr),
          position:positions(
            id, code, name_fr,
            subsector:subsectors(id, name_fr, category)
          )
        )
      `)
      .eq('family_id', familyId);
    if (fallback.error) throw error;
    return (fallback.data || []).map((r: any) => mapFamilyMember(r.skills));
  }

  return (data || []).map((r: any) => mapFamilyMember(r.skills));
}

function mapFamilyMember(skill: any): FamilyMember {
  const base = mapRowToRefItem(skill || {});
  const sector = skill?.sector;
  const position = skill?.position;
  const subsector = position?.subsector;
  return {
    ...base,
    sectorName: sector?.name_fr || undefined,
    sectorCode: sector?.code || undefined,
    positionName: position?.name_fr || undefined,
    positionCode: position?.code || undefined,
    subsectorName: subsector
      ? (subsector.category ? `${subsector.category} – ${subsector.name_fr}` : subsector.name_fr)
      : undefined,
  };
}

export async function addSkillToFamily(skillId: string, familyId: string): Promise<void> {
  const { error } = await supabase
    .from('skill_family_members')
    .insert({ skill_id: skillId, family_id: familyId });

  if (error) throw error;
  logger.functional('add_skill_to_family', 'referentialService', { skillId, familyId });
}

export async function removeSkillFromFamily(skillId: string, familyId: string): Promise<void> {
  const { error } = await supabase
    .from('skill_family_members')
    .delete()
    .eq('skill_id', skillId)
    .eq('family_id', familyId);

  if (error) throw error;
  logger.functional('remove_skill_from_family', 'referentialService', { skillId, familyId });
}

export async function createSkillFamily(family: { code: string; nameFr: string; nameEn?: string; description?: string }): Promise<SkillFamily> {
  const { data, error } = await supabase
    .from('skill_families')
    .insert({
      code: family.code,
      name_fr: family.nameFr,
      name_en: family.nameEn || null,
      description: family.description || null,
      source: 'manual',
      review_status: 'confirmed',
    })
    .select()
    .single();

  if (error) throw error;
  triggerTranslation('skill_families', [data.id]);
  return mapSkillFamily({ ...data, skill_family_members: [] });
}

export async function updateSkillFamily(id: string, updates: {
  nameFr?: string;
  nameEn?: string;
  nameAr?: string;
  nameTr?: string;
  nameEs?: string;
  namePt?: string;
  nameVi?: string;
  description?: string;
  active?: boolean;
}): Promise<void> {
  const row: Record<string, any> = {};
  if (updates.nameFr !== undefined) row.name_fr = updates.nameFr;
  if (updates.nameEn !== undefined) row.name_en = updates.nameEn;
  if (updates.nameAr !== undefined) row.name_ar = updates.nameAr;
  if (updates.nameTr !== undefined) row.name_tr = updates.nameTr;
  if (updates.nameEs !== undefined) row.name_es = updates.nameEs;
  if (updates.namePt !== undefined) row.name_pt = updates.namePt;
  if (updates.nameVi !== undefined) row.name_vi = updates.nameVi;
  if (updates.description !== undefined) row.description = updates.description;
  if (updates.active !== undefined) row.active = updates.active;

  const { error } = await supabase
    .from('skill_families')
    .update(row)
    .eq('id', id);

  if (error) throw error;
  if (updates.nameFr !== undefined) {
    triggerTranslation('skill_families', [id]);
  }
}

// ─── Skill Usage Stats ───────────────────────────────────────────────

export async function fetchSkillUsageStats(limit = 50): Promise<SkillUsageStat[]> {
  const { data, error } = await supabase
    .from('skill_usage_stats')
    .select('*, skills(name_fr)')
    .order('checked_count', { ascending: false })
    .limit(limit);

  if (error) throw error;

  return (data || []).map((r: any) => ({
    skillId: r.skill_id,
    skillName: r.skills?.name_fr || '',
    checkedCount: r.checked_count || 0,
    uncheckedCount: r.unchecked_count || 0,
    ratio: r.checked_count > 0 ? r.checked_count / (r.checked_count + r.unchecked_count) : 0,
    lastUpdated: r.last_updated,
  }));
}

// ─── Skill Proximity Pairs ──────────────────────────────────────────

export async function fetchProximityPairs(status?: string): Promise<SkillProximityPair[]> {
  let query = supabase
    .from('skill_proximity_pairs')
    .select(`
      id, similarity, status, reviewed_at,
      skill_a:skills!skill_proximity_pairs_skill_a_id_fkey(id, name_fr, code),
      skill_b:skills!skill_proximity_pairs_skill_b_id_fkey(id, name_fr, code)
    `)
    .order('similarity', { ascending: false });

  if (status) query = query.eq('status', status);

  const { data, error } = await query;
  if (error) throw error;

  return (data || []).map((r: any) => ({
    id: r.id,
    skillA: { id: r.skill_a.id, nameFr: r.skill_a.name_fr, code: r.skill_a.code },
    skillB: { id: r.skill_b.id, nameFr: r.skill_b.name_fr, code: r.skill_b.code },
    similarity: r.similarity,
    status: r.status,
    reviewedAt: r.reviewed_at,
  }));
}

export async function updateProximityPairStatus(
  id: string,
  status: 'confirmed' | 'merged' | 'dismissed'
): Promise<void> {
  const { error } = await supabase
    .from('skill_proximity_pairs')
    .update({
      status,
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', id);

  if (error) throw error;
  logger.functional('update_proximity_status', 'referentialService', { id, status });
}

// ─── Translation ─────────────────────────────────────────────────────

type TranslatableTable = 'sectors' | 'subsectors' | 'positions' | 'skills' | 'skill_groups' | 'skill_families' | 'certifications';

export async function updateTranslation(
  table: RefTable,
  id: string,
  lang: string,
  value: string,
  lock: boolean = true,
): Promise<void> {
  const updateData: Record<string, any> = { [`name_${lang}`]: value };

  // If locking, update the translation_locked JSONB
  if (lock) {
    const { data: current } = await supabase.from(table).select('translation_locked').eq('id', id).single();
    const locked = current?.translation_locked || {};
    locked[lang] = true;
    updateData.translation_locked = locked;
  }

  const { error } = await supabase.from(table).update(updateData).eq('id', id);
  if (error) {
    logger.error('update_translation', 'referentialService', { table, id, lang, error: error.message });
    throw error;
  }
}

export async function toggleTranslationLock(
  table: RefTable,
  id: string,
  lang: string,
  locked: boolean,
): Promise<Record<string, boolean>> {
  const { data: current } = await supabase.from(table).select('translation_locked').eq('id', id).single();
  const lockState = current?.translation_locked || {};
  lockState[lang] = locked;
  if (!locked) delete lockState[lang];

  const { error } = await supabase.from(table).update({ translation_locked: Object.keys(lockState).length > 0 ? lockState : null }).eq('id', id);
  if (error) throw error;
  return lockState;
}

export async function triggerTranslation(
  table: TranslatableTable,
  ids: string[],
  langs?: string[],
): Promise<{ translated: number; total: number; error?: string; errors?: string[] } | null> {
  if (ids.length === 0) return null;
  try {
    const { data, error } = await supabase.functions.invoke('translate-referential', {
      body: langs?.length ? { table, ids, langs } : { table, ids },
    });
    if (error) {
      logger.error('trigger_translation', 'referentialService', { table, ids, error: String(error) });
      // Le corps d'erreur 4xx est souvent dans data malgré error non-null
      const msg = (data as any)?.error || error.message || String(error);
      return { translated: 0, total: ids.length, error: msg };
    }
    if ((data as any)?.error) {
      logger.error('trigger_translation', 'referentialService', { table, ids, error: (data as any).error });
      return { translated: 0, total: ids.length, error: String((data as any).error), errors: (data as any).errors };
    }
    logger.technical('trigger_translation_ok', 'referentialService', { table, count: ids.length, result: data });
    return data as { translated: number; total: number; errors?: string[] };
  } catch (err) {
    logger.error('trigger_translation', 'referentialService', { table, error: String(err) });
    return { translated: 0, total: ids.length, error: String(err) };
  }
}

// ─── Translation review & glossary ──────────────────────────────────

export interface GlossaryEntry {
  id: string;
  termFr: string;
  lang: string;
  term: string;
  note: string;
}

export async function fetchGlossary(lang?: string): Promise<GlossaryEntry[]> {
  let query = supabase
    .from('translation_glossary')
    .select('*')
    .order('term_fr', { ascending: true });
  if (lang) query = query.eq('lang', lang);

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map((r: any) => ({
    id: r.id,
    termFr: r.term_fr,
    lang: r.lang,
    term: r.term,
    note: r.note || '',
  }));
}

export async function upsertGlossaryEntry(entry: { termFr: string; lang: string; term: string; note?: string }): Promise<void> {
  const { error } = await supabase
    .from('translation_glossary')
    .upsert(
      { term_fr: entry.termFr.trim(), lang: entry.lang, term: entry.term.trim(), note: entry.note || null },
      { onConflict: 'term_fr,lang' },
    );
  if (error) throw error;
  logger.functional('upsert_glossary_entry', 'referentialService', { termFr: entry.termFr, lang: entry.lang });
}

export async function deleteGlossaryEntry(id: string): Promise<void> {
  const { error } = await supabase.from('translation_glossary').delete().eq('id', id);
  if (error) throw error;
}

/**
 * Échantillon aléatoire d'items traduits pour revue par langue.
 * Sur-échantillonne puis mélange côté client (pas de random() via PostgREST).
 */
export async function fetchTranslationSample(
  table: RefTable,
  lang: string,
  limit = 30,
): Promise<RefItem[]> {
  const { count } = await supabase
    .from(table)
    .select('id', { count: 'exact', head: true })
    .not(`name_${lang}`, 'is', null);
  const total = count || 0;
  if (total === 0) return [];

  const offset = total > limit * 4
    ? Math.floor(Math.random() * (total - limit * 4))
    : 0;

  const { data, error } = await supabase
    .from(table)
    .select('*')
    .not(`name_${lang}`, 'is', null)
    .order('id')
    .range(offset, offset + limit * 4 - 1);
  if (error) throw error;

  const mapped = (data || []).map(mapRowToRefItem);
  for (let i = mapped.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [mapped[i], mapped[j]] = [mapped[j], mapped[i]];
  }
  return mapped.slice(0, limit);
}

/** Tous les ids d'une table (paginé) — pour les campagnes de re-traduction. */
export async function fetchAllIds(table: RefTable): Promise<string[]> {
  const PAGE = 1000;
  const ids: string[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from(table)
      .select('id')
      .order('id')
      .range(from, from + PAGE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    ids.push(...data.map((r: any) => r.id as string));
    if (data.length < PAGE) break;
  }
  return ids;
}

// ─── Quick Create (inline add) ──────────────────────────────────────

function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

export async function quickCreateSkill(
  nameFr: string,
  groupId: string,
  positionId: string,
  sectorId: string,
  positionCode?: string,
  sortOrder = 0
): Promise<RefItem> {
  const prefix = positionCode || 'skill';
  const code = `${slugify(prefix)}_${slugify(nameFr)}`;

  const { data, error } = await supabase
    .from('skills')
    .insert({
      code,
      name_fr: nameFr.trim(),
      skill_group_id: groupId,
      position_id: positionId,
      sector_id: sectorId,
      active: true,
      sort_order: sortOrder,
    })
    .select()
    .single();

  if (error) throw error;
  logger.functional('quick_create_skill', 'referentialService', { code, nameFr });
  triggerTranslation('skills', [data.id]);
  return mapRowToRefItem(data);
}

export async function quickCreateGroup(
  nameFr: string,
  positionId: string,
  positionCode?: string,
  sortOrder = 0
): Promise<RefItem> {
  const prefix = positionCode || 'grp';
  const code = `${slugify(prefix)}_g_${slugify(nameFr)}`;

  const { data, error } = await supabase
    .from('skill_groups')
    .insert({
      code,
      name_fr: nameFr.trim(),
      position_id: positionId,
      active: true,
      sort_order: sortOrder,
    })
    .select()
    .single();

  if (error) throw error;
  logger.functional('quick_create_group', 'referentialService', { code, nameFr });
  triggerTranslation('skill_groups', [data.id]);
  return mapRowToRefItem(data);
}

export async function quickCreatePosition(
  nameFr: string,
  sectorId: string,
  subsectorId?: string,
  sortOrder = 0
): Promise<RefItem> {
  const code = `pos_${slugify(nameFr)}`;

  const { data, error } = await supabase
    .from('positions')
    .insert({
      code,
      name_fr: nameFr.trim(),
      sector_id: sectorId,
      subsector_id: subsectorId || null,
      active: true,
      sort_order: sortOrder,
    })
    .select()
    .single();

  if (error) throw error;
  logger.functional('quick_create_position', 'referentialService', { code, nameFr });
  triggerTranslation('positions', [data.id]);
  return mapRowToRefItem(data);
}

// ─── Helpers ─────────────────────────────────────────────────────────

export async function fetchPositionIdByCode(code: string): Promise<string | null> {
  const { data } = await supabase
    .from('positions')
    .select('id')
    .eq('code', code)
    .single();
  return data?.id || null;
}

export async function fetchAllSkills(): Promise<RefItem[]> {
  const { data, error } = await supabase
    .from('skills')
    .select('*')
    .eq('active', true)
    .order('name_fr', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapRowToRefItem);
}
